/* Header navigation behaviour: hover-to-open sub-menus (mega menu / dropdown),
   page overlay while a menu is open, and the tab switcher inside the
   "tabs with products" mega menu. Keyboard toggling of the <details> and the
   close-on-focus-out logic stay with Dawn's <header-menu> element. */

const HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const CLOSE_DELAY_MS = 120;
const MENU_OPEN_CLASS = 'header--menu-open';

class HeaderNavigation extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('.header-wrapper');
    this.overlay = this.wrapper ? this.wrapper.querySelector('[data-header-overlay]') : null;
    this.menus = Array.from(this.querySelectorAll('details[data-hover-menu]'));
    this.hoverQuery = window.matchMedia(HOVER_MEDIA_QUERY);
    this.closeTimer = null;

    this.onKeyDown = (event) => {
      if (event.key === 'Escape') this.closeAll();
    };
    this.onOverlayClick = () => this.closeAll();

    this.menus.forEach((details) => {
      const summary = details.querySelector('summary');
      details.addEventListener('toggle', () => this.onToggle(details));
      details.addEventListener('mouseenter', () => this.onEnter(details));
      details.addEventListener('mouseleave', () => this.onLeave(details));
      summary.addEventListener('click', (event) => this.onSummaryClick(event, details));
    });

    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeyDown);
    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    window.clearTimeout(this.closeTimer);
  }

  onEnter(details) {
    if (!this.hoverQuery.matches) return;
    window.clearTimeout(this.closeTimer);
    this.open(details);
  }

  onLeave(details) {
    if (!this.hoverQuery.matches) return;
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(details), CLOSE_DELAY_MS);
  }

  /* With a mouse the menu is already open on hover, so clicking the label
     follows its own link (if it has one) instead of collapsing the menu. */
  onSummaryClick(event, details) {
    const url = event.currentTarget.dataset.link;
    const hasLink = url && url !== '#' && url !== '';
    if (!this.hoverQuery.matches || !details.open || !hasLink) return;

    event.preventDefault();
    window.location.href = url;
  }

  onToggle(details) {
    const summary = details.querySelector('summary');
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');

    const anyOpen = this.menus.some((menu) => menu.open);
    if (!this.wrapper) return;

    this.wrapper.classList.toggle(MENU_OPEN_CLASS, anyOpen);
    // Read by Dawn's sticky header so it doesn't hide while a menu is open.
    this.wrapper.preventHide = anyOpen;
  }

  open(details) {
    this.menus.forEach((menu) => {
      if (menu !== details && menu.open) menu.open = false;
    });
    if (!details.open) details.open = true;
  }

  close(details) {
    if (details.open) details.open = false;
  }

  closeAll() {
    this.menus.forEach((menu) => this.close(menu));
  }
}

customElements.define('header-navigation', HeaderNavigation);

class MenuTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.select(tab));
      tab.addEventListener('keydown', (event) => this.onKeyDown(event, tab));
    });
  }

  onKeyDown(event, tab) {
    const direction = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
    if (!direction) return;

    event.preventDefault();
    const index = this.tabs.indexOf(tab);
    const next = this.tabs[(index + direction + this.tabs.length) % this.tabs.length];
    this.select(next);
    next.focus();
  }

  select(activeTab) {
    this.tabs.forEach((tab) => {
      const selected = tab === activeTab;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));

      tab.classList.toggle('mega-menu__nav-item--selected', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) {
        tab.removeAttribute('tabindex');
      } else {
        tab.setAttribute('tabindex', '-1');
      }
      if (panel) panel.hidden = !selected;
    });
  }
}

customElements.define('menu-tabs', MenuTabs);
